<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <!-- 検索条件 -->
          <v-form @submit.prevent="search" ref="formRefs">
            <v-row>
              <v-col cols="3">
                <v-select density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.siteCode"
                  :items="siteCodeList" item-title="code_name" item-value="code" :label="$t('sitezone.label.siteCode')"
                  @update:modelValue="searchCondition.zoneCode = null" return-object />
              </v-col>
              <v-col cols="3">
                <v-select density="compact" variant="outlined" hide-details="auto" v-model="searchCondition.zoneCode"
                  :items="zoneItem" item-title="name" item-value="code" :label="$t('sitezone.label.zoneCode')"
                  :clearable="true" />
              </v-col>
              <v-col cols="auto">
                <v-checkbox density="compact" v-model="searchCondition.hasParentZone"
                  :label="$t('sitezone.label.hasParentZoneCheckbox')" />
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto" class="d-flex align-center">
                <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
                <v-btn theme="dark" class="ml-4 px-8 bg-grey-darken-3" elevation="2" @click.prevent="search"
                  :loading="isSearching">
                  {{ $t('common.action.search') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <!-- 検索結果テーブル -->
          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th v-for="header in headers" :key="header" @click.stop="tableSort(header)"
                      :style="cursorCheck(header)">
                      {{ $t("sitezone.label.table." + header) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in filteredDataList" :key="data.siteCode + data.zoneCode">
                    <template v-for="header in headers" :key="header">
                      <td>{{ data[header] }}</td>
                    </template>
                    <td>
                      <v-btn v-if="data.parentZone && data.parentZone.trim() !== ''" size="small" color="error"
                        @click="confirmDelete(data)">
                        {{ $t('common.action.delete') }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <!-- ページング -->
          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="currentPageNo" :length="maxPageNo" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select density="compact" variant="outlined" hide-details="auto" v-model="displayPerPage"
                    :items="displayPerPageItem" item-title="name" item-value="value"
                    :label="$t('common.label.displayCountPerPage')" @update:modelValue="resetCondition()"
                    return-object />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayContentNo.minNo }} - {{ displayContentNo.maxNo }}{{ $t('common.label.displayedDataRange') }} /
                  {{ dataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- CSVダウンロードボタン -->
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn dark class="px-8 bg-indigo-darken-1" elevation="2" @click.prevent="downloadCsv"
                :disabled="dataList.length == 0">
                {{ $t('common.action.outputCsv') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog" />
        <ZoneDeleteDialog v-if="deleteDialogVisible" :dialog-data="deleteDialog" :handle-ok="confirmDeleteOk"
          :handle-cancel="confirmDeleteCancel" @delete-success="handleDeleteSuccess"
          @delete-error="handleDeleteError" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import api from "@/apis/staff";
import utils from "@/utils";
import constant from "@/utils/constant";
import AppWrapperView from "@/components/views/AppWrapperView";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import ZoneDeleteDialog from "@/components/dialog/ZoneDeleteDialog";

export default {
  components: {
    AppWrapperView,
    ZeroData,
    ZoneDeleteDialog
  },
  props: {},
  computed: {
    constant() {
      return constant;
    },
    // ソート処理
    sortedTable() {
      let tempArray = this.dataList.map(x => x);
      // ソート条件がある場合
      if (this.sortState.target != "") {
        return tempArray.sort(this.defaultSorter(this.sortState.ascent));
      }
      return tempArray;
    },
    // ページング関連処理
    displayContentNo() {
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * (this.currentPageNo);
      if (maxNo > this.dataList.length || this.displayPerPage.value == 0) {
        return { minNo: minNo, maxNo: this.dataList.length };
      }
      return { minNo: minNo, maxNo: maxNo };
    },
    // 最大ページ
    maxPageNo() {
      if (this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.dataList.length / this.displayPerPage.value);
    },
    // ページ区切り後のデータ
    filteredDataList() {
      const tempArray = this.sortedTable;
      return tempArray.slice(this.displayContentNo.minNo - 1, this.displayContentNo.maxNo);
    },
    // ヘッダー
    headers() {
      return this.$getTableDef("SitezonePage");
    },
    // ゾーンリスト
    zoneItem() {
      if (!this.searchCondition.siteCode) return [];
      const selectedShop = this.siteCodeList.find(x => x.code == this.searchCondition.siteCode.code);
      if (selectedShop == null || selectedShop.zoneList == null) return [];
      return selectedShop.zoneList;
    },
  },

  data: () => ({
    // 表示ページ
    currentPageNo: 1,
    // フォーム参照
    formRefs: null,
    //１ページ表示数
    displayPerPage: {},
    displayPerPageItem: [],
    // ソートの状態
    sortState: { target: "", ascent: true },
    // 検索結果格納
    dataList: [],
    // 検索条件
    searchCondition: {
      siteCode: null,        // 店舗コード
      zoneCode: null,        // ゾーンコード
    },
    // 店舗リスト
    siteCodeList: [],
    // 検索中
    isSearching: false,
    // 0件取得時の警告ダイアログ
    zeroDataWarningDialogFlg: false,
    // 汎用メッセージダイアログ
    commonMsgDlgVisible: false,
    data4MsgDialog: {
      message: "",
      errorList: [],
    },
    // 削除ダイアログ表示フラグ
    deleteDialogVisible: false,
    // 削除ダイアログに渡すデータ
    deleteDialog: {
      zone: null,
    },
  }),

  methods: {
    // 店舗リスト取得
    getValidShopSearchList() {
      api.post("/store/search", { selectSiteFlag: 1 })
        .then((response) => {
          if (response.status == 1) return;
          response.data.results?.shopList.forEach(element => {
            this.siteCodeList.push({
              name: element.name,
              code: element.code,
              zoneList: element.zoneList,
              code_name: element.code + '_' + element.name
            })
          })
          if (this.siteCodeList.length > 0) {
            this.searchCondition.siteCode = this.siteCodeList[0];
          }
        })
        .catch(() => {
          // do nothing
        })
    },
    //テーブルの条件リセット
    resetCondition() {
      this.currentPageNo = 1;
      this.sortState.target = "";
      this.sortState.ascent = true;
    },
    // カーソルの変更
    cursorCheck() {
      return "cursor: pointer;"
    },
    // 検索条件クリア
    clear() {
      this.searchCondition = {
        siteCode: this.siteCodeList.length > 0 ? this.siteCodeList[0] : null,
        zoneCode: "",
        hasParentZone: false,
      }
    },
    // 検索処理
    search() {
      this.isSearching = true;
      api.post("/zone/search", {
        siteCode: this.searchCondition.siteCode?.code ?? "",
        zoneCode: this.searchCondition.zoneCode ?? "",
        hasParentZone: this.searchCondition.hasParentZone
      })
        .then((response) => {
          this.dataList = response.data.results?.sitezoneList ?? [];
          this.resetCondition();
          // 0件ダイアログ処理
          if (this.dataList.length == 0) this.zeroDataWarningDialogFlg = true;
          this.isSearching = false;
        })
        .catch(() => {
          // 0件 dialog
          this.dataList = [];
          this.zeroDataWarningDialogFlg = true;
          this.isSearching = false;
        })
    },
    // ソート関連処理
    tableSort(headValue) {
      if (this.sortState.target == headValue) {
        this.sortState.ascent = !this.sortState.ascent;
      } else {
        this.sortState.target = headValue;
        this.sortState.ascent = true;
      }
    },
    // 汎用的なソート関数を返却する
    defaultSorter(isAscent) {
      return (a, b) => {
        const multiplier = isAscent ? 1 : -1;
        if (a[this.sortState.target] > b[this.sortState.target]) return 1 * multiplier;
        if (a[this.sortState.target] < b[this.sortState.target]) return -1 * multiplier;
        return 0;
      }
    },

    // 削除ダイアログ表示
    confirmDelete(item) {
      this.deleteDialogVisible = true;
      this.deleteDialog.zone = item;
    },

    // 削除ダイアログ非表示
    closeDeleteDialog() {
      this.deleteDialogVisible = false;
      this.deleteDialog.zone = null;
    },

    // 削除ダイアログキャンセル
    confirmDeleteCancel() {
      this.closeDeleteDialog();
    },

    // 削除ダイアログOK押下（削除成功時）
    confirmDeleteOk() {
      this.closeDeleteDialog();
      this.search();
    },

    // CSVダウンロード処理
    downloadCsv() {
      // 親ゾーンを持つゾーンだけをフィルタリング
      const filteredData = this.sortedTable.filter(item => item.parentZone && item.parentZone.trim() !== '');

      // ヘッダーとデータの両方をダブルクォートで囲む
      utils.quotedSimpleDownloadCsv(
        ['zoneCode'],
        filteredData,
        "sitezone.csvHeader.",
        'sitezone.fileNamePrefix.csv',
        this.$t
      );
    },
    // 汎用ダイアログ関連処理
    // 0件ダイアログを閉じる
    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },
    // 共通メッセージダイアログを閉じる
    closeMessageDataDialog() {
      this.commonMsgDlgVisible = false;
    },
  },
  created() {
    this.getValidShopSearchList();
  },
  mounted() {
    this.displayPerPage = utils.getDisplayPerPage(this.$t)
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);
  },
};


</script>